import React from "react";
//#region types
import { DrillDownTableRowProps } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import DrillDownTableRowTypeSwitch from "./rowTypeSwitch";
import { TableEnums } from "models/enums";
import DrillDownTableRowDimensionChildren from "./rowDimensionChildren";
import DrillDownTableRowChildren from "./rowChildren";
import DrillDownTableEmptyRow from "./emptyRow";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

enum DrillDownChildRowType {
  Dimension,
  Other,
  None,
}

const DrillDownTableRow: React.FC<DrillDownTableRowProps> = (props) => {
  const { rowDisplayType, row, childrenKey, sumValueRow, autoExpandProps, index, path } = props;

  const [expanded, setExpanded] = React.useState(false);

  const children = row[childrenKey] ?? [];
  const hasChildren = children.length > 0;

  const getRowDiplayType = (row: any) => (rowDisplayType ? rowDisplayType(row) : undefined);
  const displayType = getRowDiplayType(row);

  React.useEffect(() => {
    if (displayType === TableEnums.RowDisplayType.DimensionTitle) setExpanded(true);
  }, [displayType]);

  const toggleExpansion = () => setExpanded(!expanded);

  const dimensionChildren = children.filter((childRow: any) => {
    const type = getRowDiplayType(childRow);
    return type && [TableEnums.RowDisplayType.DimensionTitle, TableEnums.RowDisplayType.Dimension].includes(type);
  });

  const nonDimensionChildren = children.filter((childRow: any) => {
    const type = getRowDiplayType(childRow);
    return (
      type === undefined ||
      ![TableEnums.RowDisplayType.DimensionTitle, TableEnums.RowDisplayType.Dimension].includes(type)
    );
  });

  const getChildrenType = () => {
    if (!hasChildren) return DrillDownChildRowType.None;

    const isLeafLevel = displayType === TableEnums.RowDisplayType.Leaf;

    if (isLeafLevel) {
      if (dimensionChildren) return DrillDownChildRowType.Dimension;
      else return DrillDownChildRowType.Other;
    }

    const onlyDimensionChildren = nonDimensionChildren.length === 0;

    if (onlyDimensionChildren) {
      if (expanded) return DrillDownChildRowType.Dimension;
      else return DrillDownChildRowType.None;
    }

    if (expanded) return DrillDownChildRowType.Other;
    else return DrillDownChildRowType.Dimension;
  };

  const childrenType = getChildrenType();

  React.useEffect(() => {
    if (autoExpandProps?.expandAll !== undefined) {
      setExpanded(autoExpandProps.expandAll);
    }

    if (
      autoExpandProps?.expandIndex !== undefined &&
      autoExpandProps.expandIndex.index === index &&
      JSON.stringify(autoExpandProps.expandIndex.path ?? []) === JSON.stringify(path)
    ) {
      setExpanded(true);
    }
  }, [autoExpandProps]);

  return (
    <>
      <DrillDownTableRowTypeSwitch
        {...props}
        onClickRow={hasChildren ? toggleExpansion : undefined}
        expandable={hasChildren}
        expanded={expanded}
        displayType={displayType}
      />
      {childrenType === DrillDownChildRowType.Dimension ? (
        <DrillDownTableRowDimensionChildren
          {...props}
          rows={dimensionChildren}
          className={
            props.className?.includes(cssExports.sumDimension) || displayType === TableEnums.RowDisplayType.Sum
              ? cssExports.sumDimension
              : ""
          }
          sumValueRow={displayType === TableEnums.RowDisplayType.Sum ? row : sumValueRow}
        />
      ) : childrenType === DrillDownChildRowType.Other ? (
        <DrillDownTableRowChildren {...props} rows={nonDimensionChildren} />
      ) : (
        <></>
      )}

      {/* Add empty row for spacing after sum rows*/}
      {!!displayType && [TableEnums.RowDisplayType.Sum, TableEnums.RowDisplayType.SumSmall].includes(displayType) && (
        <DrillDownTableEmptyRow
          {...props}
          showTotal={!!props.showTotal}
          className={clsx(
            cssExports.spacingRow,
            displayType === TableEnums.RowDisplayType.Sum ? cssExports.sumSpacing : ""
          )}
          sumValueRow={displayType === TableEnums.RowDisplayType.Sum ? row : undefined}
        />
      )}
    </>
  );
};

export default DrillDownTableRow;
